/**
 * What the shell knows about work already in progress.
 *
 * The sidebar's resume card (F-NAV-09), the Ringside quick action (F-NAV-08),
 * the New-entry row (F-NAV-10) and the status block (F-NAV-11) all branch on
 * effort and journal state that belongs to the effort store (P15) and the
 * journal repository (P17). The shell must not read either directly — it does
 * not own their semantics and could not fail closed correctly on their behalf —
 * so it reads this narrow projection instead, and those packets publish it.
 *
 * The default is not a placeholder. "No effort in progress, journal library not
 * yet available" is a real state the product has on a first launch, and every
 * surface listed above has documented copy for exactly that case.
 */

import type { ReactNode } from "react";
import { createContext, useContext } from "react";

/**
 * `finishedUnsaved` is separate from `paused` because F-NAV-09 tints only this
 * one `warning`: the effort is over and the record is not yet safe, which is
 * the single state in the shell that is asking for something rather than
 * offering it.
 */
export type ShellOpenEffortState = "active" | "paused" | "finishedUnsaved";

export interface ShellOpenEffort {
  /** The activity name, already resolved to what the user typed or chose. */
  readonly activity: string;
  readonly state: ShellOpenEffortState;
}

export type ShellStructuredEffortState = "active" | "paused";

export interface ShellStructuredEffort {
  readonly planName: string;
  readonly state: ShellStructuredEffortState;
}

export interface ShellActivity {
  readonly openEffort: ShellOpenEffort | null;
  readonly structured: ShellStructuredEffort | null;
  /**
   * F-NAV-10: the New-entry row *disappears* rather than disabling when the
   * journal library is unavailable, so presence is the whole question — which
   * is why this is the action itself rather than an availability flag beside
   * one. A flag and a handler are two ways to say the same thing, and the row
   * would eventually render from one while calling the other.
   */
  readonly createJournalEntry: (() => void) | null;
}

export const NO_SHELL_ACTIVITY: ShellActivity = {
  openEffort: null,
  structured: null,
  createJournalEntry: null,
};

const ShellActivityContext = createContext<ShellActivity>(NO_SHELL_ACTIVITY);

export function useShellActivity(): ShellActivity {
  return useContext(ShellActivityContext);
}

export interface ShellActivityProviderProps {
  readonly activity: ShellActivity;
  readonly children?: ReactNode;
}

export function ShellActivityProvider({
  activity,
  children,
}: ShellActivityProviderProps): ReactNode {
  return (
    <ShellActivityContext.Provider value={activity}>{children}</ShellActivityContext.Provider>
  );
}
